import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

export function formatLastSeen(dateString) {
  if (!dateString) return 'Offline';
  const now = new Date();
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Offline';
  const diffMs = now - date;

  if (diffMs < 60000) return 'Aktiv tani';
  if (diffMs < 3600000) return `Aktiv para ${Math.floor(diffMs / 60000)}m`;
  if (diffMs < 86400000) return `Aktiv para ${Math.floor(diffMs / 3600000)}h`;
  if (diffMs < 172800000) return 'Aktiv dje';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function usePresence(currentUser, activeConversationId = null) {
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [typingMap, setTypingMap] = useState({});
  const channelRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const activeTypingConversationRef = useRef(null);
  const typingExpiryTimersRef = useRef(new Map());

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    const typingExpiryTimers = typingExpiryTimersRef.current;
    const channel = supabase.channel('german-chat-presence', {
      config: { presence: { key: currentUser.id } },
    });
    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const online = new Set(Object.keys(channel.presenceState()));
        online.add(currentUser.id);
        setOnlineUserIds(online);
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        setOnlineUserIds((previous) => new Set([...previous, key]));
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        setOnlineUserIds((previous) => {
          const next = new Set(previous);
          if (channel.presenceState()[key]?.length) next.add(key);
          else next.delete(key);
          return next;
        });
      })
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (
          !payload?.conversationId
          || !payload.userId
          || payload.userId === currentUser.id
        ) return;

        const conversationId = String(payload.conversationId);
        const userId = String(payload.userId);
        const timerKey = `${conversationId}:${userId}`;
        const existingTimer = typingExpiryTimers.get(timerKey);
        if (existingTimer) {
          clearTimeout(existingTimer);
          typingExpiryTimers.delete(timerKey);
        }

        const updateTypingState = (isTyping) => {
          setTypingMap((previous) => {
            const current = new Set(previous[conversationId] || []);
            if (isTyping) current.add(userId);
            else current.delete(userId);

            const next = { ...previous };
            if (current.size) next[conversationId] = current;
            else delete next[conversationId];
            return next;
          });
        };

        updateTypingState(Boolean(payload.isTyping));
        if (payload.isTyping) {
          const timer = setTimeout(() => {
            typingExpiryTimers.delete(timerKey);
            updateTypingState(false);
          }, 4000);
          typingExpiryTimers.set(timerKey, timer);
        }
      })
      .subscribe((status, error) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('Unable to subscribe to chat presence:', error || status);
        }
      });

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      activeTypingConversationRef.current = null;
      typingExpiryTimers.forEach((timer) => clearTimeout(timer));
      typingExpiryTimers.clear();
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [currentUser?.id, currentUser?.username, currentUser?.email]);

  const sendTyping = useCallback(
    (conversationId, isTyping = true) => {
      if (!channelRef.current || !conversationId || !currentUser?.id) return;
      activeTypingConversationRef.current = isTyping ? String(conversationId) : null;
      channelRef.current.send({
        type: 'broadcast',
        event: 'typing',
        payload: {
          conversationId,
          userId: currentUser.id,
          username: currentUser.username || 'Student',
          isTyping,
        },
      });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          if (activeTypingConversationRef.current === String(conversationId)) {
            sendTyping(conversationId, false);
          }
        }, 2500);
      }
    },
    [currentUser?.id, currentUser?.username]
  );

  useEffect(() => {
    const typingConversationId = activeTypingConversationRef.current;
    if (typingConversationId && typingConversationId !== String(activeConversationId || '')) {
      sendTyping(typingConversationId, false);
    }
  }, [activeConversationId, sendTyping]);

  const isUserOnline = useCallback(
    (targetUserId) => {
      if (!targetUserId) return false;
      if (targetUserId === currentUser?.id) return true;
      return onlineUserIds.has(targetUserId);
    },
    [currentUser?.id, onlineUserIds]
  );

  const isConversationTyping = useCallback(
    (conversationId) => {
      if (!conversationId) return false;
      return Boolean(typingMap[conversationId]?.size);
    },
    [typingMap]
  );

  const getTypingUserName = useCallback(
    (conversationId) => {
      if (!conversationId) return null;
      return typingMap[conversationId]?.size ? 'Nxënësi po shkruan...' : null;
    },
    [typingMap]
  );

  return {
    onlineUserIds,
    isUserOnline,
    isConversationTyping,
    getTypingUserName,
    sendTyping,
    formatLastSeen,
  };
}

export default usePresence;
